"use client";

import Link from "@/components/no-prefetch-link";
import Image from "next/image";
import { useState, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Clock,
  CalendarDays,
  Search,
  BarChart3,
  Star,
  Cloud,
  Globe,
  Layers,
  GitCompare,
  Menu,
  User,
  LogOut,
  Heart,
  Bell,
  CornerDownLeft,
  LoaderCircle,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { secureFetch } from "@/lib/security/fetch-client";
import { useAuth } from "@/components/auth-provider";
import { trackSearchAndGo, trackSearchSuggestionAndGo } from "@/lib/analytics";
import { SubscriptionDrawer } from "@/components/subscription-drawer";
import { useAuthDialog } from "@/components/auth-dialog-provider";

const navItems = [
  { href: "/", label: "热搜", icon: Clock },
  { href: "/daily", label: "日榜", icon: CalendarDays },
  // { href: "/topics", label: "专题", icon: Layers }, // 暂时隐藏，数据展示待优化
  { href: "/wordcloud", label: "词云", icon: Cloud },
  { href: "/celebrity", label: "明星", icon: Star },
  { href: "/city", label: "地区", icon: Globe },
  // { href: "/compare", label: "对比", icon: GitCompare }, // 暂时下线，功能待完善
  // { href: "/ai-insights", label: "AI分析", icon: BarChart3 }, // 暂时下线，功能待完善
];

/** 关注导航项 —— 打开订阅抽屉而非跳转页面 */
const FOLLOW_NAV = { label: "关注", icon: Heart };

export function Header() {
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<Array<{ id: string; name: string }>>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsFailed, setSuggestionsFailed] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [subDrawerOpen, setSubDrawerOpen] = useState(false);
  const { openAuth } = useAuthDialog();
  const pathname = usePathname();
  const { user, logout } = useAuth();
  const searchRef = useRef<HTMLDivElement>(null);
  const requestSequence = useRef(0);
  const suggestionCache = useRef(new Map<string, {
    expiresAt: number;
    data: Array<{ id: string; name: string }>;
  }>());

  // 搜索建议
  useEffect(() => {
    const query = searchQuery.trim();
    const sequence = ++requestSequence.current;
    if (query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      setSuggestionsLoading(false);
      setSuggestionsFailed(false);
      setActiveSuggestion(-1);
      return;
    }

    setSuggestionsLoading(true);
    setSuggestionsFailed(false);
    setShowSuggestions(true);
    const cached = suggestionCache.current.get(query);
    if (cached && cached.expiresAt > Date.now()) {
      setSuggestions(cached.data);
      setSuggestionsLoading(false);
      setActiveSuggestion(-1);
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      try {
        const data = await secureFetch<{ data: Array<{ id: string; name: string }> }>(
          "/api/search/suggest",
          {
            method: "GET",
            params: { q: query, limit: 6 },
            signal: controller.signal,
          },
        );
        if (sequence !== requestSequence.current) return;
        const nextSuggestions = data.data?.map((r) => ({ id: r.id, name: r.name })) || [];
        suggestionCache.current.set(query, {
          expiresAt: Date.now() + 30_000,
          data: nextSuggestions,
        });
        setSuggestions(nextSuggestions);
        setShowSuggestions(true);
        setActiveSuggestion(-1);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
        if (sequence !== requestSequence.current) return;
        setSuggestions([]);
        setSuggestionsFailed(true);
        setShowSuggestions(true);
      } finally {
        if (sequence === requestSequence.current) setSuggestionsLoading(false);
      }
    }, 350);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [searchQuery]);

  // 点击外部关闭建议
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      setShowSuggestions(false);
      trackSearchAndGo(q, `/search?q=${encodeURIComponent(q)}`);
    }
  };

  const handleSuggestionClick = (suggestion: { id: string; name: string }, index: number) => {
    setShowSuggestions(false);
    trackSearchSuggestionAndGo(suggestion.name, index, `/topic/${suggestion.id}`);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveSuggestion((current) => Math.min(current + 1, suggestions.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveSuggestion((current) => Math.max(current - 1, -1));
    } else if (event.key === "Enter" && activeSuggestion >= 0) {
      event.preventDefault();
      handleSuggestionClick(suggestions[activeSuggestion], activeSuggestion);
    } else if (event.key === "Escape") {
      setShowSuggestions(false);
      setActiveSuggestion(-1);
    }
  };

  const isActive = (href: string) => {
    if (href === "/") {
      return pathname === "/";
    }
    return pathname.startsWith(href);
  };

  return (
    <header className="sticky top-0 z-50 border-b bg-background/95 shadow-[0_1px_0_rgba(15,23,42,0.03)]">
      <div className="container mx-auto flex h-12 items-center justify-between gap-2 px-3 sm:h-14 sm:gap-3 sm:px-4">
        {/* Logo */}
        <Link href="/" className="flex flex-shrink-0 items-center gap-1.5 sm:gap-2">
          <Image
            src="/favicon.png"
            alt="热搜时光机"
            width={32}
            height={32}
            className="h-6 w-6 rounded-md sm:h-7 sm:w-7"
            priority
          />
          <span className="relative inline-block text-sm font-bold text-slate-950 dark:text-white sm:text-lg">
            热搜时光机
            <span className="absolute -right-3 -top-2 rounded bg-slate-950 px-1 py-px text-[7px] font-bold leading-none text-white shadow-sm dark:bg-white dark:text-slate-950 sm:-right-3.5 sm:-top-2 sm:text-[8px]">
              AI
            </span>
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden items-center gap-0.5 lg:flex">
          {navItems.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={`flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors ${
                isActive(item.href)
                  ? "bg-slate-100 text-slate-950 shadow-[inset_0_0_0_1px_rgba(15,23,42,0.04)] dark:bg-slate-800 dark:text-white"
                  : "text-slate-700 hover:bg-slate-100/70 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
              }`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </Link>
          ))}
          <button
            type="button"
            onClick={() => setSubDrawerOpen(true)}
            className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100/70 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            <FOLLOW_NAV.icon className="w-4 h-4" />
            {FOLLOW_NAV.label}
          </button>
        </nav>

        {/* Search with suggestions */}
        <div ref={searchRef} className="relative min-w-0 flex-1 lg:max-w-sm xl:max-w-md">
          <form onSubmit={handleSearch} className="flex items-center gap-1 sm:gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="搜索历史热搜"
                className="h-8 w-full rounded-lg pl-8 text-sm shadow-none sm:h-9 sm:pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery.trim().length >= 2 && setShowSuggestions(true)}
                onKeyDown={handleSearchKeyDown}
                role="combobox"
                aria-label="搜索历史热搜"
                aria-autocomplete="list"
                aria-controls="header-search-suggestions"
                aria-expanded={showSuggestions && !!searchQuery.trim()}
                aria-activedescendant={activeSuggestion >= 0 ? `header-search-option-${activeSuggestion}` : undefined}
              />
            </div>
            <Button type="submit" size="sm" className="hidden h-8 px-3 sm:inline-flex sm:h-9">
              搜索
            </Button>
          </form>

          {/* Search suggestions dropdown */}
          {showSuggestions && !!searchQuery.trim() && (
            <div
              id="header-search-suggestions"
              role="listbox"
              aria-label="搜索建议"
              className="absolute left-0 right-0 top-full z-50 mt-1 max-h-80 overflow-y-auto rounded-xl border bg-background p-1.5 shadow-xl"
            >
              {suggestionsLoading ? (
                <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground" role="status">
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                  正在查找包含“{searchQuery.trim()}”的词条
                </div>
              ) : suggestions.length > 0 ? (
                <>
                  <div className="px-3 pb-1.5 pt-1 text-xs text-muted-foreground">
                    包含“{searchQuery.trim()}”的历史热搜
                  </div>
                  {suggestions.map((suggestion, i) => (
                  <button
                    key={suggestion.id}
                    id={`header-search-option-${i}`}
                    role="option"
                    aria-selected={activeSuggestion === i}
                    type="button"
                    className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${
                      activeSuggestion === i ? "bg-muted" : "hover:bg-muted"
                    }`}
                    onMouseEnter={() => setActiveSuggestion(i)}
                    onClick={() => handleSuggestionClick(suggestion, i)}
                  >
                    <span className="min-w-0 truncate">{suggestion.name}</span>
                    <CornerDownLeft className="ml-3 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  </button>
                  ))}
                  <button
                    type="button"
                    className="mt-1 w-full rounded-lg border-t px-3 py-2.5 text-left text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                    onClick={() => {
                      setShowSuggestions(false);
                      trackSearchAndGo(searchQuery.trim(), `/search?q=${encodeURIComponent(searchQuery.trim())}`);
                    }}
                  >
                    查看全部搜索结果
                  </button>
                </>
              ) : (
                <div className="px-3 py-3 text-sm text-muted-foreground" role="status">
                  {suggestionsFailed ? "搜索暂时不可用，请稍后重试" : `没有找到包含“${searchQuery.trim()}”的词条`}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Desktop User Menu */}
        <div className="hidden flex-shrink-0 items-center gap-1.5 lg:flex">
          {user ? (
            <div className="flex items-center gap-1.5">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSubDrawerOpen(true)} title="我的订阅">
                <Bell className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2" onClick={() => setSubDrawerOpen(true)}>
                <span className="text-sm max-w-[100px] truncate">{user.username}</span>
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={logout} title="退出登录">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSubDrawerOpen(true)} title="订阅热搜">
                <Bell className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2" onClick={() => openAuth({ mode: "login" })}>
                <User className="w-4 h-4" />
                登录
              </Button>
              <Button size="sm" variant="outline" className="h-8 px-3" onClick={() => openAuth({ mode: "register" })}>
                注册
              </Button>
            </div>
          )}
        </div>

        {/* Mobile Menu */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <Button variant="ghost" size="icon" className="h-9 w-9">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-64">
            <SheetHeader>
              <SheetTitle className="sr-only">导航菜单</SheetTitle>
            </SheetHeader>
            <div className="flex flex-col gap-2 mt-4">
              {/* 用户区域 */}
              <div className="px-4 py-3 border-b mb-2">
                {user ? (
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => { setMobileMenuOpen(false); setSubDrawerOpen(true); }}
                      className="flex items-center gap-1.5 text-sm font-medium hover:text-primary transition-colors"
                    >
                      <Bell className="w-4 h-4" />
                      {user.username}
                    </button>
                    <Button variant="ghost" size="sm" onClick={() => { logout(); setMobileMenuOpen(false); }}>
                      <LogOut className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => { setMobileMenuOpen(false); setSubDrawerOpen(true); }}
                      className="flex items-center justify-center gap-1.5 text-sm font-medium hover:text-primary transition-colors py-2"
                    >
                      <Bell className="w-4 h-4" />
                      订阅热搜提醒
                    </button>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => { setMobileMenuOpen(false); openAuth({ mode: "login" }); }}>
                        <User className="w-3.5 h-3.5" /> 登录
                      </Button>
                      <Button size="sm" className="flex-1" onClick={() => { setMobileMenuOpen(false); openAuth({ mode: "register" }); }}>注册</Button>
                    </div>
                  </div>
                )}
              </div>
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-3 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${
                    isActive(item.href)
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-muted"
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Link>
              ))}
              <button
                type="button"
                onClick={() => { setMobileMenuOpen(false); setSubDrawerOpen(true); }}
                className="px-4 py-3 text-sm font-medium rounded-md transition-colors flex items-center gap-2 hover:bg-muted"
              >
                <FOLLOW_NAV.icon className="w-4 h-4" />
                {FOLLOW_NAV.label}
              </button>
              <a
                href="https://jq.qq.com/?_wv=1027&k=nZIcjTG7"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-3 text-sm font-medium rounded-md transition-colors flex items-center gap-2 hover:bg-muted text-muted-foreground"
              >
                <span className="text-base">★</span>
                QQ交流群
              </a>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* 订阅管理侧边抽屉 */}
      <SubscriptionDrawer open={subDrawerOpen} onOpenChange={setSubDrawerOpen} />

      {/* 登录/注册弹窗由全局 AuthDialogProvider 统一挂载 */}
    </header>
  );
}
